import React from 'react';
import {injectIntl} from 'react-intl';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import {
  ComposedChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  ResponsiveContainer,
  Legend,
  Brush,
  Area
} from 'recharts';

import {
  LEADS_BY_PERIOD_QUERY, LEAD_BROWSER_BY_PERIOD, LEAD_OS_BY_PERIOD, LEAD_DEVICE_TYPE_BY_PERIOD,
  LEAD_DURATION_BY_PERIOD_QUERY, LEAD_AGE_BY_PERIOD_QUERY, LEAD_CLOSURE_BY_PERIOD,
  CONSUMER_ORIGIN_BY_PERIOD_QUERY, LEAD_LINEAGE_BY_PERIOD_QUERY, LEAD_SCORING_BY_PERIOD,
  LEAD_RELATIVE_CLOSURE_BY_PERIOD, LEADS_GFM
} from './gql/query'
import {chooseColor, chooseScoreColor, getTotalAmount, 
  setData, getUnit, toPercent, prepareGrouppedData, prepareGFMData} from "./utils";
import {Loading} from "../../utils/loading";

const noPercentTooltip = [
  'Total leads', 
  "Total good", 
  'Total high risk', 
  'Total low risk', 
  'Total middle risk',
  'Всего хороших',
  'Всего с низким риском',
  'Всего со средним риском',
  'Всего с высоким риском',
  'Все лиды',
]

const toValue = (data, id, name) => {
  const value = Object.values(data).find(({ created }) => created === id)[name] ||
    Object.values(data).find(({ created }) => created === id).leadsCount
  return value || 0
}

const CustomTooltip = props => {
  const { active } = props;

  if (active) {
    const { payload, totalAmount, data, label } = props;
    return (
      <div className="custom-tooltip">
        <p>{label}</p>
        <ul>
          {payload && payload.map(({color, name, payload}, i) => {
            const valueAbs = toValue(data, payload.created, name)
            let valuePercent = `(${toPercent(data, payload.created, 
              (totalAmount[payload.created] && totalAmount[payload.created][name]) 
              || totalAmount[payload.created] || totalAmount, 'created', name)})`
            if(~noPercentTooltip.indexOf(name) || valueAbs === 0)  valuePercent = ''
            return (
            <li key={i} style={{color}}>
              <span>{name}</span>
              <span>: </span>
              <span>{`${valueAbs} ${valuePercent}`}</span>
            </li>)})}
        </ul>
      </div>
    );
  }

  return null;
}

const XTick = (props) => {
  const {x, y, width, height, stroke, fill, payload, textAnchor} = props;
  return (<text
    height={height}
    width={width}
    x={x} y={y+12}
    stroke={stroke}
    fill={fill}
    className="recharts-text recharts-cartesian-axis-tick-value"
    textAnchor={textAnchor}
  >
    <tspan x={x} dy="0.71em">{payload.value}</tspan>
  </text>)
}

const YTick = (props) => {
  const {x, y, width, height, stroke, fill, payload, textAnchor, unit} = props;
  return (<text
    height={height}
    width={width}
    x={x-12} y={y}
    stroke={stroke}
    fill={fill}
    className="recharts-text recharts-cartesian-axis-tick-value"
    textAnchor={textAnchor}
  >
    <tspan x={x-12} dy="0.355em">{payload.value + (unit === 'percent'? '%': '')}</tspan>
  </text>)
}


class LineBarChart extends React.Component {

  static propTypes = {
    metric: PropTypes.string.isRequired,
    dateFrom: PropTypes.object.isRequired,
    dateTo: PropTypes.object.isRequired,
    groups: PropTypes.array,
    projects: PropTypes.array,
    labelType: PropTypes.string,
    labelValues: PropTypes.object,
    mode: PropTypes.string,
    scaling: PropTypes.bool,
    unit: PropTypes.string,
  }

  static defaultProps = {
    scaling: false,
    mode: 'line'
  }

  constructor(props) {
    super(props)
    this.state = {
      activeTitle: null
    }
  }

  onMouseEnter = (o) => {
    this.setState({ activeTitle: o.value });
  }

  onMouseLeave = (o) => {
    this.setState({ activeTitle: null });
  }

  _renderSeriesData(dataKey, name, color) {
    const {mode, unit, metric} = this.props;
    if(metric === 'leads-gfm')
      return (
        <Area 
          unit={getUnit(unit)}
          isAnimationActive={false}
          type='monotone'
          key={name}
          dataKey={dataKey}
          name={name}
          stroke={color}
          fill={color}
          strokeWidth={1}
          strokeOpacity={this.state.activeTitle === name ? 0.7 : 1}
          fillOpacity={this.state.activeTitle === name ? 1 : 0.05}
          connectNulls={true}
          dot={{ fill: '#ffffff', r: 2 }}
          activeDot={{ r: 2}}
        />)

    if (mode === 'bar') {
      return <Bar
        unit={getUnit(unit)}
        isAnimationActive={false}
        key={name}
        dataKey={dataKey}
        name={name}
        stroke={color}
        fill={color}
        strokeWidth={2}
        strokeOpacity={this.state.activeTitle === name ? 0.7 : 1}
        fillOpacity={this.state.activeTitle === name ? 0.7 : 1}
        connectNulls={true}
      />
    } else {
      return <Line
        unit={getUnit(unit)}
        isAnimationActive={false}
        type='monotone'
        key={name}
        dataKey={dataKey}
        name={name}
        stroke={color}
        fill={color}
        strokeWidth={2}
        strokeOpacity={this.state.activeTitle === name ? 0.7 : 1}
        fillOpacity={this.state.activeTitle === name ? 0.7 : 1}
        connectNulls={true}
        dot={{ fill: '#ffffff', r: 2 }}
        activeDot={{ r: 2}}
      />
    }
  }

  _renderLeadSeries() {
    const {formatMessage} = this.props.intl;
    return this._renderSeriesData(
      'leadsCount',
      formatMessage({ id: 'analytics.leads-count', defaultMessage: 'Leads Count'}),
      chooseColor(true),
    );
  }

  _renderLeadAgeSeries() {
    const {formatMessage} = this.props.intl;
    return this._renderSeriesData(
      'leadsCount',
      formatMessage({ id: 'analytics.leads-count', defaultMessage: 'Leads Count'}),
      chooseColor(true),
    );
  }

  _renderMultipleSeries(groups) {
    let res = [];
    for (let i = 0; i < groups.length; i++) {
      let group = groups[i];
      let color = !~['score', 'lead-closure', 'lead-closure-relative'].indexOf(this.props.metric)
        ?chooseColor(i === 0)
        :chooseScoreColor(group)
      res.push(this._renderSeriesData(group, group, color));
    }
    return res;
  }

  _renderSeries(metric, data) {
    switch (metric) {
      case 'leads':
        return this._renderLeadSeries();
      default:
        return this._renderMultipleSeries(data.groups)
    }
  }

  _renderChart() {
    const {metric, data, scaling, unit} = this.props;
    let totalAmount = getTotalAmount(data, metric)
    const chartData = setData(data, totalAmount, unit, metric)
    return (
      <ResponsiveContainer>
        <ComposedChart data={chartData}>
          <XAxis
            dataKey={'created'}
            height={30}
            tick={<XTick />}
          />
          <YAxis domain={['auto', 'auto']} tick={<YTick unit={unit}/>} />
          <Tooltip content={<CustomTooltip data={data.data} totalAmount={totalAmount} unit={getUnit(unit)}/>}/>
          <CartesianGrid vertical={false}/>
          { this._renderSeries(metric, data) }
          { scaling && <Brush dataKey='created' height={20} stroke="#4ab8bb"/> }
          <Legend
            verticalAlign="top"
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            wrapperStyle={{top: '-7px'}}
          />
        </ComposedChart>
      </ResponsiveContainer>
    )
  }

  render() {
    return this.props.data.loading ? <Loading /> : this._renderChart();
  }
}


export default compose(
  graphql(LEADS_BY_PERIOD_QUERY, {
    options: ({
      dateFrom, dateTo, projects, labelType, labelValues,
      osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }) => {
      return {
        variables: {
          dateFrom, dateTo, projects, labelType, labelValues,
          osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        },
        notifyOnNetworkStatusChange: true
      };
    },
    skip: (props) => (props.metric !== 'leads')
  }),
  graphql(LEAD_AGE_BY_PERIOD_QUERY, {
    options: ({
      dateFrom, dateTo, projects, labelType, labelValues,
      osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }) => {
      return {
        variables: {
          dateFrom, dateTo, projects, labelType, labelValues,
          osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        },
        notifyOnNetworkStatusChange: true
      };
    },
    skip: (props) => (props.metric !== 'lead-age'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      let res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
  graphql(LEAD_DURATION_BY_PERIOD_QUERY, {
    options: ({
      dateFrom, dateTo, groups, projects, labelType, labelValues,
      osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }) => {
      return {
        variables: {
          dateFrom, dateTo, groups, projects, labelType, labelValues,
          osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        },
        notifyOnNetworkStatusChange: true
      };
    },
    skip: (props) => (props.metric !== 'lead-duration'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      let res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
  graphql(CONSUMER_ORIGIN_BY_PERIOD_QUERY, {
    options: ({
      metric, dateFrom, dateTo, groups, projects, labelType,
      labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }) => {
      const bySubnet = metric === 'consumer-origin-by-ip';
      return {
        variables: {
          dateFrom, dateTo, groups, projects, bySubnet, labelType, labelValues,
          osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        },
        notifyOnNetworkStatusChange: true
      };
    },
    skip: (props) => {
      return -1 === ['consumer-origin-by-ip', 'consumer-origin-by-device'].indexOf(props.metric);
    },
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      let res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
  graphql(LEAD_LINEAGE_BY_PERIOD_QUERY, {
    options: ({
      metric, dateFrom, dateTo, projects, labelType, labelValues,
      osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
    }) => {
      let leadOwner = 'all';
      if (metric === 'lead-lineage-you') {
        leadOwner = 'you';
      } else if (metric === 'lead-lineage-other') {
        leadOwner = 'other';
      }
      return {
        variables: {
          dateFrom, dateTo, projects, labelType, labelValues,
          osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region, leadOwner
        },
        notifyOnNetworkStatusChange: true
      };
    },
    skip: (props) => {
      const metrics = ['lead-lineage', 'lead-lineage-you', 'lead-lineage-other'];
      return -1 === metrics.indexOf(props.metric);
    },
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      let res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),

  graphql(LEAD_BROWSER_BY_PERIOD, {
    options: ({metric, dateFrom, dateTo, projects, labelType, labelValues,
                        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      const isMobile = metric === 'lead-browsers-mobile';
      return {
        variables: {
          dateFrom, dateTo, isMobile, projects, labelType,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: props => (props.metric !== 'lead-browsers-mobile' && props.metric !== 'lead-browsers-desktop'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      let res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),

  graphql(LEAD_OS_BY_PERIOD, {
    options: ({metric, dateFrom, dateTo, projects, labelType, labelValues,
                        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      const isMobile = metric === 'lead-os-mobile';
      return {
        variables: {
          dateFrom, dateTo, isMobile, projects, labelType,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: props => (props.metric !== 'lead-os-mobile' && props.metric !== 'lead-os-desktop'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      let res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
  graphql(LEAD_DEVICE_TYPE_BY_PERIOD, {
    options: ({dateFrom, dateTo, projects, labelType, labelValues,
                        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      return {
        variables: {
          dateFrom, dateTo, projects, labelType,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: props => (props.metric !== 'lead-device-types'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      let res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
  graphql(LEAD_SCORING_BY_PERIOD, {
    options: ({dateFrom, dateTo, projects, labelType, labelValues, scoringTypes = ["good", "low", "mid", "high"],
                        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      return {
        variables: {
          dateFrom, dateTo, projects, labelType, scoringTypes,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: props => (props.metric !== 'score'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
  graphql(LEAD_CLOSURE_BY_PERIOD, {
    options: ({dateFrom, dateTo, projects, labelType, labelValues, scoringTypes = ["good"],
    osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      return {
        variables: {
          dateFrom, dateTo, projects, labelType, scoringTypes,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: props => (props.metric !== 'lead-closure'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
  graphql(LEAD_RELATIVE_CLOSURE_BY_PERIOD, {
    options: ({dateFrom, dateTo, projects, labelType, labelValues, scoringTypes = ["good"],
    osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      return {
        variables: {
          dateFrom, dateTo, projects, labelType, scoringTypes,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: props => (props.metric !== 'lead-closure-relative'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = prepareGrouppedData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
  graphql(LEADS_GFM, {
    options: ({dateFrom, dateTo, projects, labelType, labelValues, scalePeriod, intervals,
    osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region }) => {
      return {
        variables: {
          dateFrom, dateTo, projects, labelType, scalePeriod, intervals,
          labelValues, osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, region
        }, notifyOnNetworkStatusChange: true
      }
    },
    skip: props => (props.metric !== 'leads-gfm'),
    props: ({ ...ownProps, data: { loading, data, refetch } }) => {
      const res = prepareGFMData(data);
      return { ...ownProps, data: { loading, data: res.data, groups: res.groups, refetch }}
    }
  }),
)(injectIntl(LineBarChart));


// WEBPACK FOOTER //
// ./src/components/dashboard/analytics/line-bar-chart.js